import React, { useMemo } from "react";
import { css, Theme, useTheme } from "@emotion/react";
import { isEqual } from "lodash";

import Image from "core/Components/Image";
import useBreakpoint from "core/hooks/useBreakpoint";
import Button from "elements/Components/TopBanner/components/Button";

type Props = {
    topBannerImage: TopBannerImage,
    topBannerHeight: number,
    hasUniversalButton: boolean,
    showButton?: boolean,
};

const BannerImage: React.FC<Props> = ({ topBannerImage, topBannerHeight, hasUniversalButton, showButton = true }) => {
    const theme: Theme = useTheme();
    const { minWidth } = useBreakpoint();

    const [buttonHeight, buttonSidePadding] = useMemo(() => {
        switch (true) {
            case minWidth >= theme.breakpoints.sizes["3xl"]:
                return [54, 31];
            default:
                return [42, 22];
        }
    }, [minWidth, theme.breakpoints.sizes]);

    const image = useMemo(() => {
        if (minWidth >= theme.breakpoints.sizes["lg"]) {
            return topBannerImage.landscape || topBannerImage.portrait;
        }

        return topBannerImage.portrait || topBannerImage.landscape;
    }, [minWidth, topBannerImage, theme.breakpoints.sizes]);

    const useAFocusPoint = (isEqual(image, topBannerImage.landscape) && topBannerImage.useFocusPoint) ||
        (isEqual(image, topBannerImage.portrait) && topBannerImage.useFocusPointPortrait);

    const style = (theme: Theme) => css`
        width: 100vw;
        height: ${topBannerHeight}px;
        
        ${theme.breakpoints.down('sm')} {
            min-width: 360px;
        }
    `;

    const imageStyle = css`
         width: 100%;
         height: 100%;

        ${!useAFocusPoint && css`
            ${theme.breakpoints.down("md")} {
                > img {
                    display: flex;
                    min-height: unset !important;
                    max-height: unset !important;
                    height: unset !important;
                    width: 100% !important;
                    inset: unset !important;
                }
            }
        `}
    `;

    const buttonContainerStyle = css`
        position: relative;
        display: flex;
        align-items: end;
        align-content: end;
        flex-wrap: wrap;
        padding-left: 12%;
        padding-bottom: 100px;
        width: 50%;
        height: 100%;
        gap: 20px;
        opacity: ${showButton ? 1 : 0};
        transition: opacity 1.1s ease-in-out;
        
        ${theme.breakpoints.up("5xl")} {
            padding-bottom: 200px;
        }

        ${theme.breakpoints.down("md")} {
            justify-content: center;
            padding-left: unset;
            padding-bottom: 90px;
            width: 100%;
        }

        ${theme.breakpoints.down("sm")} {
            padding-bottom: 70px;
        }
    `;

    const altText = topBannerImage.altText || '';

    return (
        <div css={style} >
            <Image
                image={image}
                css={imageStyle}
                useFocusPoint={useAFocusPoint}
                customAltText={altText}
            />
            {!hasUniversalButton && (
                <div css={buttonContainerStyle}>
                    {topBannerImage.linkButtons.map((button: LinkButton, index: number) => (
                        <Button
                            key={`link-button-${index}`}
                            buttonData={button}
                            buttonHeight={buttonHeight}
                            buttonSidePadding={buttonSidePadding}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

export default BannerImage;
